import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Film, Clapperboard, Video, Camera, 
  Sparkles, Wand2, Palette, Users, Swords,
  Crown, Mountain, Castle, Rocket, Heart,
  Ghost, Zap, Trophy, Shield, Drama,
  BookOpen, PenTool, FileText, LayoutGrid,
  UserCircle, MessageSquare, ChevronRight,
  Star, Download, Share2, Eye, Loader2,
  ArrowRight, X, Check, Clock, Layers,
  ChevronDown, ExternalLink
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const AIRA_LOGO = '/aira-echo-logo.png';

// Genre Configuration with icons and colors
const GENRES = [
  { id: 'romance', name: 'Romance', icon: Heart, color: '#FF69B4', gradient: 'from-pink-500 to-rose-500' },
  { id: 'action', name: 'Action', icon: Swords, color: '#FF4500', gradient: 'from-red-500 to-orange-500' },
  { id: 'horror', name: 'Horror', icon: Ghost, color: '#8B0000', gradient: 'from-red-900 to-gray-900' },
  { id: 'fantasy', name: 'Fantasy', icon: Castle, color: '#9370DB', gradient: 'from-purple-500 to-indigo-500' },
  { id: 'thriller', name: 'Thriller', icon: Zap, color: '#4169E1', gradient: 'from-blue-600 to-slate-900' },
  { id: 'sport', name: 'Sport', icon: Trophy, color: '#32CD32', gradient: 'from-green-500 to-emerald-600' },
  { id: 'superhero', name: 'Superhero', icon: Shield, color: '#FFD700', gradient: 'from-yellow-400 to-red-500' },
  { id: 'women-empowerment', name: 'Women Empowerment', icon: Crown, color: '#FF1493', gradient: 'from-pink-600 to-purple-600' },
  { id: 'political-drama', name: 'Political Drama', icon: Drama, color: '#2F4F4F', gradient: 'from-slate-700 to-gray-800' },
  { id: 'sci-fi', name: 'Sci-Fi', icon: Rocket, color: '#00CED1', gradient: 'from-cyan-500 to-blue-600' },
];

// Combat Styles for Action Engine
const COMBAT_STYLES = [
  { id: 'boxing', name: 'Boxing', prompts: '20+', image: 'https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=800&q=80' },
  { id: 'muay-thai', name: 'Muay Thai', prompts: '20+', image: 'https://images.pexels.com/photos/11045336/pexels-photo-11045336.jpeg?auto=compress&w=800' },
  { id: 'jujitsu', name: 'Jujitsu', prompts: '20+', image: 'https://images.unsplash.com/photo-1564415315949-7a0c4c73aab4?w=800&q=80' },
  { id: 'mma', name: 'MMA Hybrid', prompts: '20+', image: 'https://images.unsplash.com/photo-1555597673-b21d5c935865?w=800&q=80' },
  { id: 'knife', name: 'Knife Combat', prompts: '20+', image: 'https://images.unsplash.com/photo-1553865541-afc89e182ae9?w=800&q=80' },
  { id: 'gun-fu', name: 'Gun-Fu Tactical', prompts: '20+', image: 'https://images.unsplash.com/photo-1509822929063-6b6cfc9b42f2?w=800&q=80' },
  { id: 'kalari', name: 'Kalari Payattu', prompts: '20+', image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80' },
  { id: 'karate', name: 'Traditional Karate', prompts: '20+', image: 'https://images.unsplash.com/photo-1555597673-b21d5c935865?w=800&q=80' },
  { id: 'samurai', name: 'Samurai Sword', prompts: '20+', image: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80' },
];

// Historical/Periodic Action Themes
const PERIODIC_THEMES = [
  { id: 'vishwaroop', name: 'Vishwaroop', era: 'Mahabharat', image: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=800&q=80' },
  { id: 'kurukshetra', name: 'Kurukshetra War', era: 'Mahabharat', image: 'https://images.unsplash.com/photo-1547751550-b62e1c2e9770?w=800&q=80' },
  { id: 'ram-setu', name: 'Ram Setu', era: 'Ramayan', image: 'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=800&q=80' },
  { id: 'sudarshan', name: 'Sudarshan Chakra', era: 'Mahabharat', image: 'https://images.unsplash.com/photo-1534670007418-fbb7f6cf32c3?w=800&q=80' },
  { id: 'shiva-tandav', name: 'Shiva Tandav', era: 'Puranic', image: 'https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=800&q=80' },
  { id: 'haldighati', name: 'Haldighati', era: '16th Century', image: 'https://images.unsplash.com/photo-1548247416-ec66f4900b2e?w=800&q=80' },
  { id: 'shivaji', name: 'Shivaji Siege', era: '17th Century', image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&q=80' },
  { id: 'virat-yudh', name: 'Virat Yudh', era: 'Mahabharat', image: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=800&q=80' },
];

// VFX Categories
const VFX_CATEGORIES = [
  { id: 'cosmic', name: 'Cosmic & Space', image: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=800&q=80' },
  { id: 'destruction', name: 'Mega Destruction', image: 'https://images.unsplash.com/photo-1514890547357-a9ee288728e0?w=800&q=80' },
  { id: 'atmospheric', name: 'Atmospheric', image: 'https://images.unsplash.com/photo-1505672678657-cc7037095e60?w=800&q=80' },
  { id: 'scifi-energy', name: 'Sci-Fi Energy', image: 'https://images.unsplash.com/photo-1535223289827-42f1e9919769?w=800&q=80' },
  { id: 'war', name: 'War & Battlefield', image: 'https://images.unsplash.com/photo-1547751550-b62e1c2e9770?w=800&q=80' },
  { id: 'divine', name: 'Divine Energy', image: 'https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=800&q=80' },
];

// 3D Character Types
const CHARACTER_3D_TYPES = [
  { id: 'human', name: 'Hyper Real Human', image: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=800&q=80' },
  { id: 'warrior', name: 'Cinematic Warrior', image: 'https://images.unsplash.com/photo-1547751550-b62e1c2e9770?w=800&q=80' },
  { id: 'period', name: 'Period Hero', image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&q=80' },
  { id: 'cosmic', name: 'Divine Cosmic', image: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=800&q=80' },
  { id: 'scifi', name: 'Sci-Fi', image: 'https://images.unsplash.com/photo-1535223289827-42f1e9919769?w=800&q=80' },
  { id: 'portrait', name: 'Emotional Portrait', image: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=800&q=80' },
];

// Script Writing Tools
const WRITING_TOOLS = [
  { id: 'story', name: 'Story Generator', desc: 'AI-powered story seeds', icon: BookOpen, image: 'https://images.unsplash.com/photo-1696479670605-aeeb206e1fcb?w=800&q=80' },
  { id: 'screenplay', name: 'Screenplay Builder', desc: 'Professional formatting', icon: FileText, image: 'https://images.unsplash.com/photo-1698208189346-6b356d242b09?w=800&q=80' },
  { id: 'storyboard', name: 'Storyboarding', desc: 'Visual shot planning', icon: LayoutGrid, image: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=800&q=80' },
  { id: 'frames', name: 'Frame Division', desc: 'Shot breakdown', icon: Layers, image: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800&q=80' },
  { id: 'character', name: 'Character Arc', desc: 'Character development', icon: UserCircle, image: 'https://images.unsplash.com/photo-1614249102574-94b6b58d02ee?w=800&q=80' },
  { id: 'dialogue', name: 'Dialogue Designer', desc: 'Natural conversations', icon: MessageSquare, image: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80' },
];

// Anime Genres with tiers
const ANIME_GENRES = [
  { id: 'action', name: 'Action', tier: 'free', desc: 'Sword clashes & martial arts', image: 'https://static.prod-images.emergentagent.com/jobs/8bd7be79-228a-48f7-b33a-8a9cd67cdac3/images/e8f7284e37a52b4dd78a48688973db0bc3797deb9e5e109e2da7dc2145e9e0ed.png' },
  { id: 'romance', name: 'Romance', tier: 'free', desc: 'Warm emotional scenes', image: 'https://static.prod-images.emergentagent.com/jobs/8bd7be79-228a-48f7-b33a-8a9cd67cdac3/images/5ff3b1711410f0626149ac931e2700fd9f5870e6f7d69b144c71c47cf7e277a4.png' },
  { id: 'thriller', name: 'Thriller', tier: 'free', desc: 'Dark suspenseful stories', image: 'https://static.prod-images.emergentagent.com/jobs/8bd7be79-228a-48f7-b33a-8a9cd67cdac3/images/862eec6311bdc24cfa2153f9783ec40918813240148182da03a372a9c2a916ce.png' },
  { id: 'horror', name: 'Horror', tier: 'free', desc: 'Eerie atmosphere', image: 'https://static.prod-images.emergentagent.com/jobs/8bd7be79-228a-48f7-b33a-8a9cd67cdac3/images/97b56641837c57338800564e969ec436d111e01ec52fc46232192548c85fb424.png' },
  { id: 'adventure', name: 'Adventure', tier: 'free', desc: 'Epic journeys', image: 'https://static.prod-images.emergentagent.com/jobs/8bd7be79-228a-48f7-b33a-8a9cd67cdac3/images/5fd22e700a3940883c5dc8df7116ff39c43a6db1579daa3b77f2046107365c3a.png' },
  { id: 'sports', name: 'Sports', tier: 'free', desc: 'High energy', image: 'https://static.prod-images.emergentagent.com/jobs/8bd7be79-228a-48f7-b33a-8a9cd67cdac3/images/3333b0186dee6bfc69ee273b87268019de00fe7b066b5efdef9c3a11bd436a71.png' },
  { id: 'samurai', name: 'Samurai', tier: 'pro', desc: 'Bushido & blade', image: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&q=80' },
  { id: 'ninja', name: 'Ninja', tier: 'pro', desc: 'Stealth & ninjutsu', image: 'https://images.unsplash.com/photo-1545389336-cf090694435e?w=800&q=80' },
  { id: 'mecha', name: 'Mecha', tier: 'pro', desc: 'Giant robot battles', image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&q=80' },
  { id: 'cyberpunk', name: 'Cyberpunk', tier: 'pro', desc: 'Neon dystopia', image: 'https://images.unsplash.com/photo-1535223289827-42f1e9919769?w=800&q=80' },
  { id: 'isekai', name: 'Isekai', tier: 'pro', desc: 'Another world', image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=800&q=80' },
  { id: 'dark-fantasy', name: 'Dark Fantasy', tier: 'pro', desc: 'Gothic worlds', image: 'https://images.unsplash.com/photo-1478760329108-5c3ed9d495a0?w=800&q=80' },
  { id: 'space-opera', name: 'Space Opera', tier: 'ultra', desc: 'Galactic adventures', image: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=800&q=80' },
  { id: 'time-travel', name: 'Time Travel', tier: 'ultra', desc: 'Temporal journeys', image: 'https://images.unsplash.com/photo-1534670007418-fbb7f6cf32c3?w=800&q=80' },
  { id: 'psychological', name: 'Psychological', tier: 'ultra', desc: 'Mind games', image: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80' },
];

// AIRA VOSS Character Sequences with thumbnails
const AIRA_VOSS_SEQUENCES = [
  { id: 1, title: 'Gothic Bridal Elegance', prompt: 'AIRA VOSS walking slowly in an ancient 17th-18th century British castle corridor. Slow motion cinematic shot. At the beginning, her head is slightly l...', quality: '8K IMAX', thumbnail: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&q=80' },
  { id: 2, title: 'Candlelit Castle Walk', prompt: 'Cinematic continuation of AIRA VOSS in the ancient British castle. Medium tracking shot following her graceful movement through the ornate corridor. S...', quality: '8K IMAX', thumbnail: 'https://images.unsplash.com/photo-1551972251-12070d63502a?w=600&q=80' },
  { id: 3, title: 'Royal Chamber Reveal', prompt: 'Final sequence of AIRA VOSS approaching the grand chamber doors. Slow push-in shot as she pauses before massive oak doors, her reflection visible in p...', quality: '8K IMAX', thumbnail: 'https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=600&q=80' },
  { id: 4, title: 'Sunset Luxury Boat Scene', prompt: 'A cinematic sunset portrait of a fictional AI-generated fashion model, seated gracefully on a luxury boat, wearing a flowing elegant white dress with ...', quality: '8K IMAX', thumbnail: 'https://images.unsplash.com/photo-1534361960057-19889db9621e?w=600&q=80' },
  { id: 5, title: 'Dutch Angle Rooftop Editorial', prompt: 'A cinematic close-up portrait of a fictional AI-generated fashion model captured with a dramatic 90-degree Dutch angle, standing on a modern rooftop t...', quality: '8K IMAX', thumbnail: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=600&q=80' },
  { id: 6, title: 'Royal Throne Portrait', prompt: 'A cinematic royal portrait of a fictional AI-generated princess seated on an ornate golden throne, wearing a luxurious embroidered gown with flowing f...', quality: '8K IMAX', thumbnail: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=600&q=80' },
  { id: 7, title: 'Royal Majestic Slow Zoom', prompt: 'A cinematic royal portrait of a fictional AI-generated princess seated gracefully on an ornate throne inside a dimly lit palace hall. Intricate gown e...', quality: '8K IMAX', thumbnail: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=600&q=80' },
];

// Section Header Component
const SectionHeader = ({ badge, title, subtitle, accentColor = '#FF007F' }) => (
  <div className="text-center mb-12">
    {badge && (
      <motion.span 
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4"
        style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
      >
        {badge}
      </motion.span>
    )}
    <motion.h2 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold text-[var(--text-primary)] mb-4"
    >
      {title}
    </motion.h2>
    {subtitle && (
      <motion.p 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        className="text-[var(--text-secondary)] max-w-2xl mx-auto"
      >
        {subtitle}
      </motion.p>
    )}
  </div>
);

// Genre Card Component
const GenreCard = ({ genre, index, onClick }) => {
  const Icon = genre.icon;
  
  return (
    <motion.button
      onClick={() => onClick(genre)}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ scale: 1.05, y: -5 }}
      whileTap={{ scale: 0.95 }}
      className="group relative overflow-hidden rounded-2xl aspect-[3/4] bg-[var(--bg-card)] border border-[var(--border-color)] hover:border-[var(--color-primary)] transition-all"
    >
      {/* Gradient Background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${genre.gradient} opacity-80`} />
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
      
      {/* Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
        <span className="text-white/60 text-xs font-medium mb-2">{index + 1}</span>
        <span className="text-white/40 text-[10px] mb-4">8K</span>
        <Icon className="w-10 h-10 text-white mb-4 group-hover:scale-110 transition-transform" />
        <h3 className="text-white font-semibold text-lg text-center">{genre.name}</h3>
        <span className="text-white/70 text-sm mt-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          Create your story <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </motion.button>
  );
};

// Feature Card Component
const FeatureCard = ({ item, onClick }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.02, y: -5 }}
    whileTap={{ scale: 0.98 }}
    className="relative overflow-hidden rounded-2xl aspect-video bg-[var(--bg-card)] border border-[var(--border-color)] hover:border-[var(--color-primary)] transition-all group"
  >
    <img 
      src={item.image} 
      alt={item.name}
      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
    
    {item.prompts && (
      <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-[var(--color-primary)]/80 text-white text-xs font-medium">
        {item.prompts} PROMPTS
      </span>
    )}
    
    {item.era && (
      <span className="absolute top-3 left-3 px-2 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">
        {item.era}
      </span>
    )}
    
    {item.tier && (
      <span className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${
        item.tier === 'free' ? 'bg-green-500/80 text-white' :
        item.tier === 'pro' ? 'bg-[var(--color-primary)]/80 text-white' :
        'bg-[var(--color-gold)]/80 text-black'
      }`}>
        {item.tier.toUpperCase()}
      </span>
    )}
    
    <div className="absolute bottom-0 left-0 right-0 p-4">
      <h3 className="text-white font-semibold text-lg">{item.name}</h3>
      {item.desc && <p className="text-white/60 text-sm mt-1">{item.desc}</p>}
    </div>
  </motion.button>
);

// Writing Tool Card
const WritingToolCard = ({ tool, onClick }) => {
  const Icon = tool.icon;
  
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.02, y: -5 }}
      whileTap={{ scale: 0.98 }}
      className="relative overflow-hidden rounded-2xl aspect-[4/3] bg-[var(--bg-card)] border border-[var(--border-color)] hover:border-[var(--color-accent)] transition-all group"
    >
      <img 
        src={tool.image} 
        alt={tool.name}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
      
      <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
        <div className="w-14 h-14 rounded-2xl bg-[var(--color-accent)]/20 border border-[var(--color-accent)]/50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
          <Icon className="w-7 h-7 text-[var(--color-accent)]" />
        </div>
        <h3 className="text-white font-semibold text-lg text-center">{tool.name}</h3>
        <p className="text-white/60 text-sm mt-1">{tool.desc}</p>
      </div>
    </motion.button>
  );
};

// Film Creation Wizard Modal
const FilmCreationWizard = ({ genre, isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [filmData, setFilmData] = useState({
    title: '',
    synopsis: '',
    duration: '90',
    style: 'cinematic',
    characters: [],
    scenes: []
  });
  
  if (!isOpen || !genre) return null;
  
  const Icon = genre.icon;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl bg-[var(--bg-secondary)] rounded-3xl border border-[var(--border-color)] overflow-hidden shadow-2xl"
        >
          {/* Header */}
          <div className={`p-6 bg-gradient-to-r ${genre.gradient}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-white text-2xl font-bold">Create {genre.name} Film</h2>
                  <p className="text-white/80 text-sm">Step {step} of 4</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-6 h-1 bg-white/20 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: '25%' }}
                animate={{ width: `${step * 25}%` }}
                className="h-full bg-white rounded-full"
              />
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-[var(--text-secondary)] text-sm mb-2">Film Title</label>
                  <input
                    type="text"
                    value={filmData.title}
                    onChange={(e) => setFilmData({ ...filmData, title: e.target.value })}
                    placeholder="Enter your film title..."
                    className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:border-[var(--color-primary)] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[var(--text-secondary)] text-sm mb-2">Synopsis</label>
                  <textarea
                    value={filmData.synopsis}
                    onChange={(e) => setFilmData({ ...filmData, synopsis: e.target.value })}
                    placeholder="Describe your story in a few sentences..."
                    rows={4}
                    className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:border-[var(--color-primary)] focus:outline-none resize-none"
                  />
                </div>
              </div>
            )}
            
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-[var(--text-secondary)] text-sm mb-2">Duration (minutes)</label>
                  <select
                    value={filmData.duration}
                    onChange={(e) => setFilmData({ ...filmData, duration: e.target.value })}
                    className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:border-[var(--color-primary)] focus:outline-none"
                  >
                    <option value="30">Short Film (30 min)</option>
                    <option value="60">Medium (60 min)</option>
                    <option value="90">Feature Film (90 min)</option>
                    <option value="120">Epic (120+ min)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[var(--text-secondary)] text-sm mb-2">Visual Style</label>
                  <div className="grid grid-cols-2 gap-3">
                    {['cinematic', 'documentary', 'experimental', 'classic'].map((style) => (
                      <button
                        key={style}
                        onClick={() => setFilmData({ ...filmData, style })}
                        className={`p-3 rounded-xl border text-center capitalize transition-all ${
                          filmData.style === style
                            ? 'bg-[var(--color-primary)]/20 border-[var(--color-primary)] text-[var(--color-primary)]'
                            : 'bg-[var(--bg-input)] border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--border-color-hover)]'
                        }`}
                      >
                        {style}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {step === 3 && (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 text-[var(--color-primary)] animate-spin mx-auto mb-4" />
                <h3 className="text-[var(--text-primary)] text-xl font-semibold mb-2">AI is generating your story...</h3>
                <p className="text-[var(--text-secondary)]">Creating characters, scenes, and dialogue</p>
              </div>
            )}
            
            {step === 4 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-[var(--text-primary)] text-xl font-semibold mb-2">Your Film Project is Ready!</h3>
                <p className="text-[var(--text-secondary)] mb-6">Start creating scenes, characters, and bring your story to life.</p>
                <button
                  onClick={onClose}
                  className="px-8 py-3 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white font-semibold hover:shadow-lg transition-all"
                >
                  Open Film Studio
                </button>
              </div>
            )}
          </div>
          
          {/* Footer */}
          {step < 3 && (
            <div className="p-6 border-t border-[var(--border-color)] flex justify-between">
              <button
                onClick={() => setStep(Math.max(1, step - 1))}
                disabled={step === 1}
                className="px-6 py-2 rounded-xl text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setStep(Math.min(4, step + 1))}
                className="px-6 py-2 rounded-xl bg-[var(--color-primary)] text-white font-medium hover:bg-[var(--color-primary)]/90 transition-colors flex items-center gap-2"
              >
                {step === 2 ? 'Generate Story' : 'Continue'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Main Film Studio Page
export default function FilmStudioPage() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  
  const handleGenreSelect = (genre) => {
    setSelectedGenre(genre);
    setShowWizard(true);
  };
  
  // Feature badges
  const badges = [
    { label: '4K+', desc: 'Ultra HD Output' },
    { label: 'AI', desc: 'Powered Creation' },
    { label: 'PRO', desc: 'Studio Quality' },
  ];

  return (
    <div 
      className="min-h-screen transition-colors duration-300"
      style={{ backgroundColor: 'var(--bg-primary)' }}
      data-testid="film-studio-page"
    >
      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-[80vh] overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0015] via-black to-[var(--bg-primary)]" />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            transition={{ duration: 2 }}
            className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]"
            style={{
              background: 'radial-gradient(circle, rgba(255, 0, 127, 0.15) 0%, transparent 70%)',
              filter: 'blur(60px)'
            }}
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-[80vh] px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm mb-6">
              <Sparkles className="w-4 h-4 text-[var(--color-primary)]" />
              Powered by AI
            </span>
            
            <img 
              src={AIRA_LOGO}
              alt="AIRA Logo"
              className="w-24 h-24 mx-auto mb-6"
            />
            
            <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-4">
              AIRA
            </h1>
            <h2 className="text-2xl md:text-4xl font-light tracking-[0.3em] text-white/80 mb-6">
              AI STUDIOS
            </h2>
            <p className="text-xl md:text-2xl text-white/60 font-light">
              The Next Generation of AI Filmmaking
            </p>
          </motion.div>
          
          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 mb-12"
          >
            <button
              onClick={() => document.getElementById('genres').scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 rounded-full bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white font-semibold hover:shadow-[0_0_40px_rgba(255,0,127,0.4)] transition-all flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Start Creating
            </button>
            <button
              onClick={() => document.getElementById('archive').scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 rounded-full bg-white/5 border border-white/20 text-white font-medium hover:bg-white/10 transition-all"
            >
              Explore Projects
            </button>
          </motion.div>
          
          {/* Badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap justify-center gap-6"
          >
            {badges.map((badge, idx) => (
              <div key={idx} className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/5 border border-white/10">
                <span className="text-[var(--color-primary)] font-bold">{badge.label}</span>
                <span className="text-white/60 text-sm">{badge.desc}</span>
              </div>
            ))}
          </motion.div>
        </div>
        
        {/* Studio Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 px-6 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-white/10"
        >
          <span className="text-white/60 text-sm tracking-wider">Studio</span>
        </motion.div>
      </section>

      {/* ==================== GENRE SELECTION ==================== */}
      <section id="genres" className="px-4 md:px-16 py-20 bg-[var(--bg-secondary)]">
        <SectionHeader 
          title="CREATE YOUR FILM"
          subtitle="Choose your genre and start creating cinematic masterpieces with AI-powered tools."
        />
        
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {GENRES.map((genre, idx) => (
            <GenreCard 
              key={genre.id} 
              genre={genre} 
              index={idx}
              onClick={handleGenreSelect}
            />
          ))}
        </div>
      </section>

      {/* ==================== AIRA VOSS - AI CHARACTER ==================== */}
      <section className="px-4 md:px-16 py-20 bg-[var(--bg-primary)]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <motion.span 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-1 rounded-full bg-[var(--color-gold)]/20 text-[var(--color-gold)] text-sm font-medium mb-4"
            >
              AI Character Universe
            </motion.span>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4"
            >
              AIRA VOSS
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-[var(--text-secondary)] mb-2 text-lg"
            >
              Digital Muse. Cinematic Intelligence.
            </motion.p>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="text-[var(--text-secondary)] max-w-2xl mx-auto"
            >
              Premium AI-generated character sequences. Gothic bridal elegance in 17th-18th century British castle corridors. 8K IMAX quality with cinematic color grading.
            </motion.p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {AIRA_VOSS_SEQUENCES.map((seq) => (
              <motion.div
                key={seq.id}
                whileHover={{ scale: 1.02 }}
                className="relative overflow-hidden rounded-2xl border border-white/10 group cursor-pointer"
              >
                <div className="aspect-[3/4] relative">
                  {/* Thumbnail Image */}
                  <img 
                    src={seq.thumbnail} 
                    alt={seq.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  
                  {/* Top Info Bar */}
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-[var(--color-gold)] flex items-center justify-center text-black text-xs font-bold">
                      {seq.id}
                    </span>
                    <span className="text-white/90 text-xs font-medium tracking-wide">AIRA VOSS</span>
                  </div>
                  
                  <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-[var(--color-gold)] text-black text-xs font-bold">
                    {seq.quality}
                  </div>
                  
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <motion.div
                      initial={{ scale: 1 }}
                      whileHover={{ scale: 1.1 }}
                      className="w-16 h-16 rounded-full bg-[var(--color-gold)]/90 flex items-center justify-center mb-3 group-hover:bg-[var(--color-gold)] transition-colors"
                    >
                      <Play className="w-8 h-8 text-black ml-1" fill="currentColor" />
                    </motion.div>
                    <span className="px-4 py-2 rounded-full bg-black/40 backdrop-blur-sm text-[var(--color-gold)] text-sm font-medium tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                      HOVER TO PLAY
                    </span>
                  </div>
                  
                  {/* Bottom Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-semibold mb-2">{seq.title}</h3>
                    <p className="text-white/60 text-xs line-clamp-2">
                      <span className="text-[var(--color-gold)]">Prompt:</span> {seq.prompt}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {['8K IMAX Output', 'Auto-Play Enabled', 'Cinematic Prompts', 'Gothic Aesthetic'].map((tag) => (
              <span key={tag} className="px-4 py-2 rounded-full bg-[var(--bg-card)] border border-[var(--border-color)] text-[var(--text-secondary)] text-sm">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== ACTION CINEMATIC ENGINE ==================== */}
      <section className="px-4 md:px-16 py-20 bg-[var(--bg-secondary)]">
        <SectionHeader 
          badge="Combat System"
          title="ACTION CINEMATIC ENGINE"
          subtitle="9 Combat Styles. 90+ Ultra Cinematic Prompts. Hollywood-level choreography with physics-accurate combat mechanics."
          accentColor="#FF4500"
        />
        
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-4">
          {COMBAT_STYLES.map((style) => (
            <FeatureCard key={style.id} item={style} onClick={() => toast.info(`${style.name} combat prompts coming soon!`)} />
          ))}
        </div>
        
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          {['8K IMAX Output', '120fps Slow Motion', 'Hollywood Choreography'].map((tag) => (
            <span key={tag} className="px-4 py-2 rounded-full bg-[var(--bg-card)] border border-[var(--border-color)] text-[var(--text-secondary)] text-sm">
              {tag}
            </span>
          ))}
        </div>
      </section>

      {/* ==================== PERIODIC ACTION CINEMA ==================== */}
      <section className="px-4 md:px-16 py-20 bg-[var(--bg-primary)]">
        <SectionHeader 
          badge="Epic Historical • ULTRA"
          title="PERIODIC ACTION CINEMA"
          subtitle="Mahabharat. Ramayan. Maratha Empire. Divine Manifestations. Epic battles recreated in 8K IMAX glory."
          accentColor="#9370DB"
        />
        
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {PERIODIC_THEMES.map((theme) => (
            <FeatureCard key={theme.id} item={theme} onClick={() => toast.info(`${theme.name} prompts coming soon!`)} />
          ))}
        </div>
      </section>

      {/* ==================== VFX LAB ==================== */}
      <section className="px-4 md:px-16 py-20 bg-[var(--bg-secondary)]">
        <SectionHeader 
          badge="Visual Effects • PRO"
          title="VFX LAB"
          subtitle="Physically Accurate. Ray-Traced. Interstellar-Level Quality. Every effect powered by 8K HDR rendering."
          accentColor="#00CED1"
        />
        
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-4">
          {VFX_CATEGORIES.map((vfx) => (
            <motion.div
              key={vfx.id}
              whileHover={{ scale: 1.02 }}
              className="relative overflow-hidden rounded-2xl aspect-video bg-[var(--bg-card)] border border-[var(--border-color)] group cursor-pointer"
            >
              <img 
                src={vfx.image} 
                alt={vfx.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-white font-semibold text-lg">{vfx.name}</h3>
                <div className="flex gap-2 mt-2">
                  <span className="px-2 py-1 rounded text-xs bg-cyan-500/20 text-cyan-400">8K ULTRA</span>
                  <span className="px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400">RAY-TRACED</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ==================== 3D CHARACTER ENGINE ==================== */}
      <section className="px-4 md:px-16 py-20 bg-[var(--bg-primary)]">
        <SectionHeader 
          badge="3D Pipeline • ULTRA"
          title="3D CHARACTER ENGINE"
          subtitle="Hyper-Realistic. Every Pore Visible. Cinema-Grade Characters with subsurface skin scattering and 8K output."
          accentColor="#FF69B4"
        />
        
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-4">
          {CHARACTER_3D_TYPES.map((char) => (
            <motion.div
              key={char.id}
              whileHover={{ scale: 1.02 }}
              className="relative overflow-hidden rounded-2xl aspect-[3/4] bg-[var(--bg-card)] border border-[var(--border-color)] group cursor-pointer"
            >
              <img 
                src={char.image} 
                alt={char.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-white font-semibold text-lg">{char.name}</h3>
                <p className="text-white/60 text-sm">8K Subsurface Rendering</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ==================== SCRIPT WRITING STUDIO ==================== */}
      <section className="px-4 md:px-16 py-20 bg-[var(--bg-secondary)]">
        <SectionHeader 
          badge="Writing Suite"
          title="SCRIPT WRITING STUDIO"
          subtitle="From Story Seed to Shooting Script. AI-powered tools for complete screenplay development."
          accentColor="#00E5FF"
        />
        
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-4">
          {WRITING_TOOLS.map((tool) => (
            <WritingToolCard key={tool.id} tool={tool} onClick={() => toast.info(`${tool.name} coming soon!`)} />
          ))}
        </div>
      </section>

      {/* ==================== 2D ANIME STUDIO ==================== */}
      <section className="px-4 md:px-16 py-20 bg-[var(--bg-primary)]">
        <SectionHeader 
          badge="50+ Genres"
          title="2D ANIME ANIMATION STUDIO"
          subtitle="50+ Genre Variations. Japanese Studio Quality. AI Powered. 9:16 vertical format, 24fps smooth animation."
          accentColor="#FF007F"
        />
        
        {/* Tier Badges */}
        <div className="flex justify-center gap-4 mb-8">
          <span className="px-4 py-2 rounded-full bg-green-500/20 text-green-400 text-sm font-medium">BASIC - Free Access</span>
          <span className="px-4 py-2 rounded-full bg-[var(--color-primary)]/20 text-[var(--color-primary)] text-sm font-medium">PRO - Pro Tier</span>
          <span className="px-4 py-2 rounded-full bg-[var(--color-gold)]/20 text-[var(--color-gold)] text-sm font-medium">ULTRA - Ultra Tier</span>
        </div>
        
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {ANIME_GENRES.map((anime) => (
            <FeatureCard key={anime.id} item={anime} onClick={() => toast.info(`${anime.name} anime studio coming soon!`)} />
          ))}
        </div>
        
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          {['24fps Smooth Animation', '9:16 Vertical Format', 'Studio Quality', '50+ Genre Presets'].map((tag) => (
            <span key={tag} className="px-4 py-2 rounded-full bg-[var(--bg-card)] border border-[var(--border-color)] text-[var(--text-secondary)] text-sm">
              {tag}
            </span>
          ))}
        </div>
      </section>

      {/* ==================== CINEMATIC ARCHIVE ==================== */}
      <section id="archive" className="px-4 md:px-16 py-20 bg-[var(--bg-secondary)]">
        <SectionHeader 
          badge="Explore"
          title="CINEMATIC ARCHIVE"
          subtitle="A curated collection of AI-generated cinematic masterpieces"
          accentColor="#FFD700"
        />
        
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {[
            { name: 'Neon Noir', image: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=600&q=80' },
            { name: 'Desert Mirage', image: 'https://images.unsplash.com/photo-1682687220742-aba13b6e50ba?w=600&q=80' },
            { name: 'Cyber Dreams', image: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=600&q=80' },
            { name: 'Golden Empire', image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&q=80' },
            { name: 'Midnight Chase', image: 'https://images.unsplash.com/photo-1509347528160-9a9e33742cdb?w=600&q=80' },
            { name: 'Ethereal Light', image: 'https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=600&q=80' },
            { name: 'Shadow Protocol', image: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=600&q=80' },
            { name: 'Aurora Rising', image: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=600&q=80' },
          ].map((item, idx) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              whileHover={{ scale: 1.05 }}
              className="relative overflow-hidden rounded-2xl aspect-[3/4] cursor-pointer group"
            >
              <img 
                src={item.image} 
                alt={item.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="text-white font-semibold">{item.name}</h3>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Film Creation Wizard Modal */}
      <FilmCreationWizard 
        genre={selectedGenre}
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
      />
    </div>
  );
}
